import { FC, FormEvent, useEffect, useMemo, useState } from 'react';
import {
  Banner,
  createBanner,
  deleteBanner,
  fetchBanners,
  updateBanner,
} from '../../api/banners';
import PaginationBar from '../../components/PaginationBar';
import { uploadBannerImage } from '../../api/bannersFiles';

const BannersPanel: FC = () => {
  const [records, setRecords] = useState<Banner[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const limit = 20;
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState<'all' | 'active' | 'inactive'>('all');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [imageUrl, setImageUrl] = useState('');
  const [actionUrl, setActionUrl] = useState('');
  const [isActive, setIsActive] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [file, setFile] = useState<File | null>(null);
  const [uploading, setUploading] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);

  const totalPages = useMemo(
    () => Math.max(1, Math.ceil(total / limit || 1)),
    [total, limit],
  );

  useEffect(() => {
    let isMounted = true;
    setLoading(true);
    setError(null);

    fetchBanners({
      page,
      limit,
      search: search.trim() || undefined,
      status,
    })
      .then((resp) => {
        if (!isMounted) return;
        setRecords(resp.data);
        setTotal(resp.total);
      })
      .catch((err) => {
        if (!isMounted) return;
        setError(
          err instanceof Error ? err.message : 'Failed to load banners.',
        );
      })
      .finally(() => {
        if (isMounted) setLoading(false);
      });

    return () => {
      isMounted = false;
    };
  }, [page, limit, search, status, refreshKey]);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!file) return;
    setSaving(true);
    setUploading(true);
    setError(null);
    try {
      const uploaded = await uploadBannerImage(file);
      await createBanner({
        imageUrl: uploaded.url,
        actionUrl: actionUrl.trim() || undefined,
        isActive,
      });
      setFile(null);
      setImageUrl(uploaded.url);
      setActionUrl('');
      setIsActive(true);
      setShowModal(false);
      setPage(1);
      setRefreshKey((k) => k + 1);
    } catch (err) {
      setError(
        err instanceof Error ? err.message : 'Failed to create banner.',
      );
    } finally {
      setSaving(false);
      setUploading(false);
    }
  };

  const handleToggleActive = async (banner: Banner) => {
    setError(null);
    try {
      await updateBanner(banner.id, { isActive: !banner.isActive });
      setRefreshKey((k) => k + 1);
    } catch (err) {
      setError(
        err instanceof Error ? err.message : 'Failed to update banner.',
      );
    }
  };

  const handleDelete = async (banner: Banner) => {
    if (!window.confirm('Remove this banner?')) return;
    setError(null);
    try {
      await deleteBanner(banner.id);
      setRefreshKey((k) => k + 1);
    } catch (err) {
      setError(
        err instanceof Error ? err.message : 'Failed to delete banner.',
      );
    }
  };

  return (
    <div className="rounded-2xl bg-white px-6 py-5 shadow-inner">
      <div className="flex flex-col gap-3 border-b border-slate-200 pb-4 sm:flex-row sm:items-center sm:justify-between">
        <div className="text-sm text-slate-600">
          Manage promotional banners shown on the order screen.
        </div>
        <button
          type="button"
          className="inline-flex items-center justify-center rounded-xl bg-primary px-4 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-primary/90 disabled:cursor-not-allowed disabled:opacity-60"
          onClick={() => {
            setShowModal(true);
            setFile(null);
            setActionUrl('');
            setIsActive(true);
            setError(null);
          }}
        >
          Add banner
        </button>
      </div>

      <div className="mt-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <input
          type="search"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search by image URL…"
          className="w-full rounded-xl border border-slate-200 bg-white px-4 py-2 text-sm text-slate-600 shadow-sm focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/30 sm:w-72"
        />
        <div className="flex items-center gap-2 text-xs text-slate-600">
          <span>Status:</span>
          <select
            value={status}
            onChange={(e) =>
              setStatus(e.target.value as 'all' | 'active' | 'inactive')
            }
            className="rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-xs focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary/30"
          >
            <option value="all">All</option>
            <option value="active">Active only</option>
            <option value="inactive">Inactive only</option>
          </select>
          {status !== 'all' && (
            <button
              type="button"
              className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-xs font-semibold text-slate-600 transition hover:border-primary/30 hover:text-primary shadow-sm"
              onClick={() => {
                setSearch('');
                setStatus('all');
                setPage(1);
              }}
            >
              <span>✕</span> Clear Filter
            </button>
          )}
        </div>
      </div>

      <div className="mt-4 overflow-hidden rounded-2xl border border-slate-200">
        <table className="min-w-full text-left text-sm text-slate-600">
          <thead className="bg-slate-50 text-xs uppercase tracking-wide text-slate-500">
            <tr>
              <th className="px-5 py-3 font-semibold">Preview</th>
              <th className="px-5 py-3 font-semibold">Image URL</th>
              <th className="px-5 py-3 font-semibold">Action URL</th>
              <th className="px-5 py-3 font-semibold">Status</th>
              <th className="px-5 py-3 font-semibold">Updated</th>
              <th className="px-5 py-3 font-semibold">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td
                  className="px-5 py-6 text-center text-slate-400"
                  colSpan={5}
                >
                  Loading banners…
                </td>
              </tr>
            ) : error ? (
              <tr>
                <td className="px-5 py-6 text-center text-danger" colSpan={5}>
                  {error}
                </td>
              </tr>
            ) : records.length === 0 ? (
              <tr>
                <td
                  className="px-5 py-6 text-center text-slate-400"
                  colSpan={5}
                >
                  No banners found.
                </td>
              </tr>
            ) : (
              records.map((banner) => (
                <tr key={banner.id} className="even:bg-slate-50/60">
                  <td className="px-5 py-3">
                    <div className="h-10 w-40 overflow-hidden rounded-lg border border-slate-200 bg-slate-50">
                      <img
                        src={banner.imageUrl}
                        alt=""
                        className="h-full w-full object-cover"
                      />
                    </div>
                  </td>
                  <td className="px-5 py-3 text-xs text-slate-600">
                    <div className="max-w-xs break-all">{banner.imageUrl}</div>
                  </td>
                  <td className="px-5 py-3 text-xs text-slate-600">
                    {banner.actionUrl ? (
                      <a
                        href={banner.actionUrl}
                        target="_blank"
                        rel="noreferrer"
                        className="max-w-xs break-all text-indigo-600 hover:underline"
                      >
                        {banner.actionUrl}
                      </a>
                    ) : (
                      <span className="text-slate-400">—</span>
                    )}
                  </td>
                  <td className="px-5 py-3">
                    <span
                      className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-semibold ${banner.isActive
                        ? 'bg-emerald-100 text-emerald-700'
                        : 'bg-slate-100 text-slate-600'
                        }`}
                    >
                      {banner.isActive ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td className="px-5 py-3 text-xs text-slate-500">
                    {new Date(banner.updatedAt).toLocaleString()}
                  </td>
                  <td className="px-5 py-3 text-xs">
                    <div className="flex items-center gap-2">
                      <button
                        type="button"
                        className="rounded-lg border border-slate-200 px-3 py-1 text-xs font-semibold text-slate-600 hover:border-primary hover:text-primary"
                        onClick={() => handleToggleActive(banner)}
                      >
                        {banner.isActive ? 'Disable' : 'Enable'}
                      </button>
                      <button
                        type="button"
                        className="rounded-lg border border-danger/30 px-3 py-1 text-xs font-semibold text-danger hover:bg-danger/5"
                        onClick={() => handleDelete(banner)}
                      >
                        Remove
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <PaginationBar
        page={page}
        totalPages={totalPages}
        onPrev={() => setPage((prev) => Math.max(1, prev - 1))}
        onNext={() => setPage((prev) => Math.min(totalPages, prev + 1))}
      />

      {
        showModal ? (
          <div className="fixed inset-0 z-40 flex items-center justify-center bg-slate-900/50 px-4">
            <div className="w-full max-w-md rounded-2xl bg-white p-6 shadow-2xl">
              <h2 className="text-lg font-semibold text-slate-900">Add banner</h2>
              {error ? (
                <div className="mt-2 rounded-lg border border-danger/30 bg-danger/10 px-3 py-2 text-xs text-danger">
                  {error}
                </div>
              ) : null}
              <form onSubmit={handleSubmit} className="mt-4 space-y-4">
                <div>
                  <label className="block text-xs font-semibold uppercase tracking-wide text-slate-500">
                    Banner image
                  </label>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={(e) => {
                      const f = e.target.files?.[0] ?? null;
                      setFile(f);
                    }}
                    className="mt-1 block w-full text-xs text-slate-600"
                  />
                  {imageUrl ? (
                    <div className="mt-3 h-20 w-full overflow-hidden rounded-lg border border-slate-200 bg-slate-50">
                      <img
                        src={imageUrl}
                        alt="Preview"
                        className="h-full w-full object-cover"
                      />
                    </div>
                  ) : null}
                </div>
                <div>
                  <label className="block text-xs font-semibold uppercase tracking-wide text-slate-500">
                    Action URL (optional)
                  </label>
                  <input
                    type="url"
                    value={actionUrl}
                    onChange={(e) => setActionUrl(e.target.value)}
                    placeholder="https://example.com/landing-page"
                    className="mt-1 w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-xs text-slate-700 shadow-sm focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/30"
                  />
                </div>
                <label className="flex items-center gap-2 text-xs font-medium text-slate-600">
                  <input
                    type="checkbox"
                    checked={isActive}
                    onChange={(e) => setIsActive(e.target.checked)}
                    className="h-4 w-4 rounded border-slate-300 text-primary focus:ring-primary"
                  />
                  Active
                </label>
                <div className="mt-4 flex justify-end gap-2">
                  <button
                    type="button"
                    className="inline-flex items-center justify-center rounded-xl border border-slate-200 px-4 py-2 text-xs font-semibold text-slate-600 hover:border-slate-400"
                    onClick={() => {
                      setShowModal(false);
                      setFile(null);
                      setImageUrl('');
                      setActionUrl('');
                      setError(null);
                    }}
                    disabled={saving || uploading}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={!file || saving || uploading}
                    className="inline-flex items-center justify-center rounded-xl bg-primary px-4 py-2 text-xs font-semibold text-white shadow-sm transition hover:bg-primary/90 disabled:cursor-not-allowed disabled:opacity-60"
                  >
                    {saving || uploading ? 'Uploading…' : 'Save banner'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        ) : null
      }
    </div >
  );
};

export default BannersPanel;
